import React from 'react';
import { motion } from 'framer-motion';

const TeacherSection = ({ onShowAllTeachers }) => {
  return (
    <section id="teachers" className="py-24 md:py-48 bg-slate-950 relative overflow-hidden text-white">
      
      {/* Background Ambient Glows */}
      <div className="absolute top-0 right-0 w-[600px] h-[600px] bg-indigo-600/10 rounded-full blur-[140px] -z-0 translate-x-1/3 -translate-y-1/2"></div>
      <div className="absolute bottom-0 left-0 w-[400px] h-[400px] bg-red-600/10 rounded-full blur-[120px] -z-0 -translate-x-1/3 translate-y-1/2"></div>

      <div className="max-w-7xl mx-auto px-6 md:px-12 relative z-10">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-16 lg:gap-24 items-center">
          
          {/* LEFT SIDE: Image with Compact Badge */}
          <motion.div 
            initial={{ opacity: 0, x: -30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            className="lg:col-span-5 relative"
          >
            {/* The Main Image Container */}
            <div className="relative z-10 rounded-[1.5rem] md:rounded-[2rem] overflow-hidden border border-white/5 shadow-2xl group">
              <img 
                src="https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?auto=format&fit=crop&q=80&w=800" 
                alt="Director" 
                className="w-full h-[500px] md:h-[650px] object-cover grayscale transition-all duration-1000 group-hover:grayscale-0 group-hover:scale-105"
              />
              
              {/* Name Overlay */}
              <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-slate-950 via-slate-950/80 to-transparent p-10 pt-20">
                 <h4 className="text-white font-black text-2xl uppercase tracking-tighter leading-none">Deshamanya <br/> <span className="text-red-600">Aruna Perera</span></h4>
                 <p className="text-[8px] font-black text-slate-500 uppercase tracking-[0.4em] mt-3 italic">Founding Director</p>
              </div>
            </div>

            {/* --- COMPACT SQUARE BADGE (Outside & Refined) --- */}
            <motion.div 
              animate={{ y: [0, -12, 0] }}
              transition={{ duration: 5, repeat: Infinity, ease: "easeInOut" }}
              className="absolute -top-6 -right-4 md:-top-10 md:-right-10 z-30 pointer-events-none"
            >
              <div className="relative">
                {/* Subtle Red Neon Glow */}
                <div className="absolute inset-0 bg-red-600 blur-[40px] opacity-30"></div>
                
                {/* The Compact Square Box */}
                <div className="relative bg-slate-900/70 backdrop-blur-3xl border border-white/20 w-32 h-32 md:w-40 md:h-40 flex flex-col items-center justify-center shadow-2xl overflow-hidden">
                  
                  {/* Minimal Corner Detail */}
                  <div className="absolute top-0 right-0 w-6 h-6 border-t-2 border-r-2 border-red-600"></div>

                  {/* 25+ Letter Size Reduced to 4xl/5xl */}
                  <span className="text-4xl md:text-5xl font-black text-white leading-none tracking-tighter flex items-start">
                    25<span className="text-red-600 text-xl md:text-2xl mt-1 ml-0.5">+</span>
                  </span>
                  
                  <div className="mt-3 flex flex-col items-center">
                    <div className="w-6 h-[1.5px] bg-red-600 mb-2"></div>
                    <span className="text-[8px] md:text-[9px] font-black text-white/80 uppercase tracking-[0.4em] text-center leading-tight">
                      Years of <br/> Legacy
                    </span>
                  </div>
                </div>
              </div>
            </motion.div>
          </motion.div>

          {/* RIGHT SIDE: Content Section */}
          <motion.div 
            initial={{ opacity: 0, x: 50 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            className="lg:col-span-7 space-y-10 md:pl-8"
          >
            <div className="space-y-6">
              <div className="inline-flex items-center gap-3 bg-white/5 border border-white/10 px-4 py-2 rounded-none">
                <span className="w-2 h-2 bg-red-600 animate-pulse rounded-full"></span>
                <span className="text-white text-[9px] font-black uppercase tracking-[0.4em]">Director's Vision 2026</span>
              </div>
              
              <h2 className="text-5xl md:text-8xl font-black text-white leading-[0.85] tracking-tighter uppercase">
                EXCELLENCE <br/> 
                WITHOUT <span className="text-transparent bg-clip-text bg-gradient-to-r from-red-600 to-red-400">COMPROMISE.</span>
              </h2>
            </div>

            <p className="text-lg md:text-2xl text-slate-400 font-medium leading-relaxed italic border-l-8 border-red-600 pl-8 py-1">
              "අපගේ අරමුණ විභාග සමත් කිරීම පමණක් නොව, ගුණගරුක සහ බුද්ධිමත් පරපුරක් බිහිකිරීමයි."
            </p>

            {/* ACTION AREA */}
            <div className="pt-6 flex flex-col sm:flex-row items-center gap-10">
              <button 
                onClick={onShowAllTeachers}
                className="group relative bg-white text-slate-950 px-12 py-6 rounded-none font-black uppercase tracking-[0.2em] text-[10px] transition-all duration-300 hover:bg-red-600 hover:text-white active:scale-95 flex items-center gap-4"
              >
                Meet the Faculty
                <span className="text-xl group-hover:translate-x-2 transition-transform duration-300">→</span>
              </button>

              <div className="flex flex-col gap-3">
                 <div className="flex -space-x-3">
                    {[1, 2, 3, 4].map((i) => (
                      <div key={i} className="w-10 h-10 border border-slate-800 overflow-hidden grayscale opacity-60">
                         <img src={`https://i.pravatar.cc/100?u=${i+60}`} className="w-full h-full object-cover" alt="expert" />
                      </div>
                    ))}
                 </div>
                 <p className="text-[8px] font-black text-slate-600 uppercase tracking-[0.4em]">Trusted Education Partner</p>
              </div>
            </div>
          </motion.div>

        </div>
      </div>
    </section>
  );
};

export default TeacherSection;